import React from "react";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { issueProxyCredential, credentialErrorMessage } from "../../service/dbProxyCredentialsService";
import { buildDbEnv, generateAlnumSecret } from "./dbAgentModel";

/**
 * Issue an install credential and build db.env with it. The secret and the
 * ProxySQL admin password live only in this call and the returned text: they
 * are never put in state, shown, or logged.
 * Resolves { ok: true, content } or { ok: false, error } (a plain message).
 */
export async function prepareDbEnv({ label, base }) {
  let credential;
  try {
    credential = await issueProxyCredential(label);
  } catch (error) {
    return {
      ok: false,
      error: credentialErrorMessage(
        error,
        "Could not issue an install credential, so db.env was not downloaded. Try again.",
      ),
    };
  }
  try {
    const content = buildDbEnv({
      base,
      clientId: credential.clientId,
      clientSecret: credential.clientSecret,
      proxysqlAdminPassword: generateAlnumSecret(32),
    });
    return { ok: true, content };
  } catch (_) {
    return { ok: false, error: "Could not build db.env in this browser. Try again in an up-to-date browser." };
  }
}

const code = {
  fontFamily: AN_MONO,
  fontSize: "0.8rem",
  padding: "0.05rem 0.3rem",
  borderRadius: "0.25rem",
  background: AN_COLORS.tint,
  border: `1px solid ${AN_COLORS.line}`,
  color: AN_COLORS.ink2,
};

/** "Secure the install" steps shown next to the db.env download. */
export const DbEnvSecuritySteps = ({ windows = false }) => (
  <div
    style={{
      marginTop: "0.75rem",
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      border: `1px solid ${AN_COLORS.line}`,
      background: AN_COLORS.bg,
      color: AN_COLORS.ink2,
      fontSize: "0.85rem",
      lineHeight: 1.6,
    }}
  >
    <div style={{ fontWeight: 600, color: AN_COLORS.ink, marginBottom: "0.25rem" }}>
      Secure the install
    </div>
    <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
      <li>
        Set ProxySQL&apos;s admin password to the value of <span style={code}>PROXYSQL_ADMIN_PASSWORD</span> in
        db.env.
      </li>
      <li>
        Bind the ProxySQL admin ports to <span style={code}>127.0.0.1</span> so they are not reachable from the
        network.
      </li>
      <li>
        {windows ? (
          <>
            Restrict db.env so only Administrators can read it (the Windows equivalent of{" "}
            <span style={code}>chmod 600 db.env</span>).
          </>
        ) : (
          <>
            Run <span style={code}>chmod 600 db.env</span>.
          </>
        )}
      </li>
    </ol>
    <div style={{ marginTop: "0.5rem", color: AN_COLORS.muted, fontSize: "0.8rem" }}>
      Each download issues a new install credential. It is shown only in this file. Revoke unused ones in
      Install credentials on the Database agents page.
    </div>
  </div>
);

/** Plain inline error under the download button. */
export const DbEnvError = ({ message }) =>
  message ? (
    <div
      role="alert"
      style={{
        marginTop: "0.75rem",
        padding: "0.5rem 0.75rem",
        borderRadius: "0.5rem",
        border: "1px solid var(--an-dangerBd)",
        background: "var(--an-dangerBg)",
        color: "var(--an-dangerFg)",
        fontSize: "0.85rem",
      }}
    >
      {message}
    </div>
  ) : null;
